import { Controller } from "@hotwired/stimulus"

/**
 * Prevents double-fires on the anonymous-headcount stepper (and any other
 * "± button" pair that hits the same endpoint via Turbo). Disables every
 * registered button while any submit is in flight; re-enables on
 * `turbo:submit-end` or `turbo:submit-error`.
 *
 * Why server-side fan-out isn't enough: each click goes through a
 * `button_to`-generated form. Without locking, two rapid clicks send two
 * concurrent PATCHes; the server applies them in arrival order against the
 * row read at the start of the request, so totals can desync from intent
 * under burst traffic.
 *
 * Usage:
 *
 *   <div data-controller="stepper-lock">
 *     <button data-stepper-lock-target="button" ...>−</button>
 *     <button data-stepper-lock-target="button" ...>+</button>
 *   </div>
 */
export default class extends Controller<HTMLElement> {
  static targets = ["button"]

  declare readonly buttonTargets: HTMLButtonElement[]

  private locked = false

  connect() {
    this.element.addEventListener("turbo:submit-start", this.lock)
    this.element.addEventListener("turbo:submit-end", this.unlock)
    this.element.addEventListener("turbo:submit-error", this.unlock)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.lock)
    this.element.removeEventListener("turbo:submit-end", this.unlock)
    this.element.removeEventListener("turbo:submit-error", this.unlock)
  }

  private lock = () => {
    if (this.locked) return
    this.locked = true
    for (const button of this.buttonTargets) {
      button.dataset.stepperLockPreviousDisabled = button.disabled ? "1" : "0"
      button.disabled = true
      button.setAttribute("aria-busy", "true")
    }
  }

  private unlock = () => {
    if (!this.locked) return
    this.locked = false
    for (const button of this.buttonTargets) {
      const wasDisabled = button.dataset.stepperLockPreviousDisabled === "1"
      button.disabled = wasDisabled
      delete button.dataset.stepperLockPreviousDisabled
      button.removeAttribute("aria-busy")
    }
  }
}
